import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { IEntity } from '../shared/models/entity';
import { forkJoin } from 'rxjs';
import { format, lastDayOfWeek } from 'date-fns';
import { PpsUtilityService } from '../services/pps-utility.service';
import { TimesheetService } from '../services/timesheet.service';
import { IEmployeeTimesheet } from '../shared/models/employee-timesheet';
import { ToastService } from '../shared/toast/toast.service';
import { IJobCode } from '../shared/models/job-code';
import { NgForm } from '@angular/forms';
import { IRosterCode } from '../shared/models/roster-code';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { SubSink } from 'subsink';
import { RosterService } from '../services/roster.service';
import { IEmployeeRoster } from '../shared/models/roster/employee-roster';
import * as _ from 'lodash';

@Component({
  selector: 'pps-manage-timesheet',
  templateUrl: './manage-timesheet.component.html',
  styleUrls: ['./manage-timesheet.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ManageTimesheetComponent implements OnInit, OnDestroy {
  private subs = new SubSink();

  parent: any = null;
  selectedEmployees:any[];

  timesheet:any = {day: new Date(), hours: 12};
  groups: IEntity[] = [];
  rosterCodes: IRosterCode[] = [];
  employees: any = [];
  groupEmployees: any =[];
  maxDate: Date;

  employeeTimesheets: IEmployeeTimesheet[] = [];
  jobCodes: IJobCode[] = [];
  allEmployeeChecked: boolean = false;
  savingTimesheet = false;

  constructor(private timesheetService: TimesheetService,
              private rosterService: RosterService,
              private confirmationService: ConfirmationService,
              private toastService: ToastService,
              public util: PpsUtilityService,
              private _dialogRef: DynamicDialogRef,
              private _dialogConfig: DynamicDialogConfig) {}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initializeDialog(){
    this.parent = this._dialogConfig.data.parent;
    this.groups = this._dialogConfig.data.groups;
    this.jobCodes = this._dialogConfig.data.jobCodes;
    this.rosterCodes = this._dialogConfig.data.rosterCodes;
    this.employees = this._dialogConfig.data.employees;
  }    

  ngOnInit(): void {
    this.maxDate = lastDayOfWeek(new Date());
    this.initializeDialog();
  }

  loadEmployeesByGroup($event){
    this.selectedEmployees = [];

    if ($event.value){
      let group = $event.value;
      this.groupEmployees = this.employees.filter(emp => emp.dateOfBirth && emp.workGroup.id === group.id);
      this.groupEmployees.forEach(e => e.checked = true);
      this.allEmployeeChecked = true;
    }else{
      this.groupEmployees = [];
    }
  }

  validateHours(validateComplete){
    var timesheet = _.cloneDeep(this.timesheet);

    if (!this.selectedEmployees){
      if (validateComplete){
        validateComplete("Please select at least one employee.");
      }
      return;
    }

    this.subs.sink = this.rosterService
      .getEmployeeRosterByEmployees(timesheet.day, this.selectedEmployees.map(e => e.id))
      .subscribe(res => {
        if (res.succeed){
          var empRosters: IEmployeeRoster[] = res.result;

          let message = {};
          this.selectedEmployees.forEach(emp => {
            let hourEntered = this.parent.getHoursEntered(emp.id, timesheet.day);
            let totalHour = hourEntered + timesheet.hours;
            timesheet.employee = emp;
            
            var empRoster = empRosters.find(e => e.employee.id === emp.id);
            var payrollRosterCode = null;
            if (empRoster){
              var rosterValue = empRoster.values[format(timesheet.day, "yyyyMMdd")];
              if (rosterValue){
                payrollRosterCode = rosterValue.prc;
              }
            }

            timesheet.payrollRosterCodeId = payrollRosterCode?.id;
            timesheet.payrollRosterCode = payrollRosterCode?.code;

            let warning = this.timesheetService.getWarningMessage(timesheet, totalHour, false);
      
            if (warning.type > 0){
              var subWarning = "";
              if (warning.type === 3){
                subWarning = `<div class="p-ml-4 p-mt-2"><span class="text-bold">${this.util.getEmployeeName(emp)}</span>: 
                  <span class='text-danger text-bold'>${totalHour}</span> hours will be logged.</div>`;
              }

              if (message.hasOwnProperty(warning.message)){
                message[warning.message].push(subWarning);
              }else{
                message[warning.message] = [subWarning];
              }
            }
          });
      
          var messageToReturn = [];

          for(var prop in message){
            messageToReturn.push(`<div class="p-mt-2">${prop}</div>`);
            if (message[prop]){
              messageToReturn.push(message[prop].join(""));
            }
          }

          if (validateComplete){
            var msg = messageToReturn.length > 0 ? `<div class="p-mb-3">Please read the following message carefully.
              Click <span class="text-danger text-bold">[ Yes ]</span> to continue to enter hours, 
              <span class="text-primary text-bold">[ No ]</span> to cancel.</div> ${messageToReturn.join("")}` : null;
            validateComplete(msg);
          }
        }
      });
  }

  saveTimesheet(form: NgForm){
    if (form.invalid){
      return;
    }

    this.validateHours((errorMessage) => {
      if (errorMessage){
        this.confirmationService.confirm({
          header: "Warning",
          message: errorMessage,
          icon: "fas fa-exclamation-triangle icon-danger p-as-start",
          defaultFocus: "accept",
          accept: () => {
            this.saveTimesheetHelper();
          }});
      }else{
        this.saveTimesheetHelper();
      }
    });
  }

  private saveTimesheetHelper(){
    let tasks = [];
    this.selectedEmployees.forEach(emp => {
      var employeeTimesheet = Object.assign({}, this.timesheet);
      employeeTimesheet.employee = {id: emp.id};
      employeeTimesheet.id = 0;
      tasks.push(this.timesheetService.addTimesheet(employeeTimesheet));
    });

    this.savingTimesheet = true;
    this.subs.sink = forkJoin(tasks)
      .subscribe(results => {
        var goodCount = 0;

        results.forEach(r => {
          goodCount += r["succeed"] ? 1 : 0
        })

        let msg = `${goodCount} timesheets saved!`;
        this.toastService.succeeded(msg, 1000);

        if (this.parent && this.parent.loadTimesheet){
          this.parent.loadTimesheet();

          results.forEach(ts => {
            window.setTimeout(() => this.parent.flashRow(ts["result"].id), 1000);
          });
        }
      })
      .add(() => {
        this.savingTimesheet = false;
        this.selectedEmployees = [];
      });
  }

  close(){
    this._dialogRef.close();
  }

  canModifyPastTimeSheet(): boolean{
    return this.parent.canModifyPastTimeSheet();
  }
}
